import React, { useState, useEffect } from 'react';
import SafetyService, { SAFETY_CATEGORIES, EMERGENCY_TYPES } from '../../services/safetyService';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  DocumentTextIcon,
  PhoneIcon,
  ClockIcon,
  UserGroupIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const SafetyPage = () => {
  const [safetyProtocols, setSafetyProtocols] = useState([]);
  const [emergencyProcedures, setEmergencyProcedures] = useState([]);
  const [safetyResources, setSafetyResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('protocols');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [selectedEmergency, setSelectedEmergency] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    loadSafetyData();
  }, []);

  const loadSafetyData = async () => {
    try {
      setLoading(true);
      const [protocols, procedures, resources] = await Promise.all([
        SafetyService.getSafetyProtocols(),
        SafetyService.getEmergencyProcedures(),
        SafetyService.getSafetyResources()
      ]);
      
      setSafetyProtocols(protocols);
      setEmergencyProcedures(procedures);
      setSafetyResources(resources);
    } catch (error) {
      console.error('Error loading safety data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      [SAFETY_CATEGORIES.CONCUSSION]: HeartIcon,
      [SAFETY_CATEGORIES.INJURY_PREVENTION]: ShieldCheckIcon,
      [SAFETY_CATEGORIES.EMERGENCY]: ExclamationTriangleIcon,
      [SAFETY_CATEGORIES.HEAT_ILLNESS]: ClockIcon,
      [SAFETY_CATEGORIES.EQUIPMENT]: UserGroupIcon,
      [SAFETY_CATEGORIES.GENERAL]: InformationCircleIcon
    };
    return icons[category] || DocumentTextIcon;
  };

  const getEmergencyIcon = (type) => {
    const icons = {
      [EMERGENCY_TYPES.MEDICAL]: HeartIcon,
      [EMERGENCY_TYPES.WEATHER]: ExclamationTriangleIcon,
      [EMERGENCY_TYPES.FACILITY]: ShieldCheckIcon,
      [EMERGENCY_TYPES.SECURITY]: UserGroupIcon
    };
    return icons[type] || DocumentTextIcon;
  };

  const ProtocolCard = ({ protocol }) => {
    const IconComponent = getCategoryIcon(protocol.category);
    const isExpanded = expandedSections[protocol.id];

    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div 
          className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(protocol.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className={`p-3 rounded-lg ${SafetyService.getPriorityColor(protocol.priority)}`}>
                <IconComponent className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {protocol.title}
                </h3>
                <p className="text-gray-600 mb-3">{protocol.description}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${SafetyService.getPriorityColor(protocol.priority)}`}>
                    {protocol.priority.toUpperCase()}
                  </span>
                  <span>Updated: {SafetyService.formatDate(protocol.lastUpdated)}</span>
                </div>
              </div>
            </div>
            <div className="ml-4">
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            {protocol.steps && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Protocol Steps</h4>
                <div className="space-y-4">
                  {protocol.steps.map((step, index) => (
                    <div key={index} className="flex space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {step.step}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 mb-2">{step.title}</h5>
                        <p className="text-gray-600 mb-3">{step.description}</p>
                        {step.details && (
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                            {step.details.map((detail, detailIndex) => (
                              <li key={detailIndex}>{detail}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {protocol.warningSigns && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-red-700 mb-3 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  Warning Signs
                </h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                    {protocol.warningSigns.map((sign, index) => (
                      <li key={index}>{sign}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {protocol.resources && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Additional Resources</h4>
                <div className="space-y-2">
                  {protocol.resources.map((resource, index) => (
                    <a
                      key={index}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      {resource.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const EmergencyCard = ({ procedure }) => {
    const IconComponent = getEmergencyIcon(procedure.type);
    const isExpanded = expandedSections[procedure.id];

    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div 
          className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(procedure.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className={`p-3 rounded-lg ${SafetyService.getPriorityColor(procedure.priority)}`}>
                <IconComponent className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {procedure.title}
                </h3>
                <p className="text-gray-600 mb-3">{procedure.description}</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${SafetyService.getPriorityColor(procedure.priority)}`}>
                  {procedure.priority.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="ml-4">
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            {procedure.steps && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Response Steps</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                  {procedure.steps.map((step, index) => (
                    <li key={index} className="text-sm">{step}</li>
                  ))}
                </ol>
              </div>
            )}
            
            {procedure.contacts && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                  <PhoneIcon className="h-5 w-5 mr-2" />
                  Emergency Contacts
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {procedure.contacts.map((contact, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-gray-900">{contact.role}</div>
                      <div className="text-blue-600 font-mono">{contact.number}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {procedure.lightningPolicy && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Lightning Policy</h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-gray-900">Suspend Distance:</span>
                      <div className="text-gray-700">{procedure.lightningPolicy.suspendDistance}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Wait Time:</span>
                      <div className="text-gray-700">{procedure.lightningPolicy.waitTime}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Safe Areas:</span>
                      <div className="text-gray-700">{procedure.lightningPolicy.safeAreas.join(', ')}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading safety information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShieldCheckIcon className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">Safety & Health Protocols</h1>
            <p className="text-xl text-red-100 max-w-3xl mx-auto">
              Comprehensive safety protocols and emergency procedures to ensure the health and well-being of all student-athletes
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('protocols')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'protocols'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Safety Protocols
            </button>
            <button
              onClick={() => setActiveTab('emergency')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'emergency'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Emergency Procedures
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'resources'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Resources
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'protocols' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Safety Protocols</h2>
              <p className="text-gray-600">
                Detailed protocols for injury prevention, concussion management, and health safety measures.
              </p>
            </div>
            
            <div className="space-y-6">
              {safetyProtocols.map(protocol => (
                <ProtocolCard key={protocol.id} protocol={protocol} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'emergency' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Emergency Procedures</h2>
              <p className="text-gray-600">
                Immediate response procedures for medical emergencies, severe weather, and facility incidents.
              </p>
            </div>
            
            <div className="space-y-6">
              {emergencyProcedures.map(procedure => (
                <EmergencyCard key={procedure.id} procedure={procedure} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Safety Resources</h2>
              <p className="text-gray-600">
                Additional resources, certifications, and contact information for safety and health.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {safetyResources.map(resource => (
                <div key={resource.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{resource.title}</h3>
                  <p className="text-gray-600 mb-4">{resource.description}</p>
                  
                  {resource.url && (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Visit Resource
                    </a>
                  )}
                  
                  {resource.provider && (
                    <div className="mt-3 text-sm text-gray-500">
                      <span className="font-medium">Provider:</span> {resource.provider}
                    </div>
                  )}
                  
                  {resource.renewalPeriod && (
                    <div className="mt-1 text-sm text-gray-500">
                      <span className="font-medium">Renewal:</span> {resource.renewalPeriod}
                    </div>
                  )}
                  
                  {resource.contacts && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-900 mb-2">Emergency Contacts</h4>
                      <div className="space-y-1">
                        {resource.contacts.map((contact, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-gray-600">{contact.service}</span>
                            <span className="font-mono text-blue-600">{contact.number}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {resource.required && (
                    <div className="mt-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Required
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyPage;